/*
 * Prestige GWP (gift with purchase) auto-add, Palo Alto theme.
 *
 * Single gift (custom.gwp_gift):
 *   - qualifying product added    -> gift added (one per qualifying unit)
 *   - qualifying product removed  -> gift removed
 *   - qualifying qty changed      -> gift qty follows
 *   - metafield cleared or offer outside its start/end dates -> gift lines removed
 *
 * Choice of gifts (2+ available gifts in custom.gwp_gift_options):
 *   - nothing is auto-added; after the machine is added a pop-up lets the customer pick one gift
 *     per machine (Claim my gift) or decline (No thanks). The X closes it for now.
 *   - if they close it without choosing, a "Final chance" reminder appears when they open the cart
 *     or press checkout. Closing that reminder with the X stops the reminders for that machine for
 *     the visit (sessionStorage gwp_dismissed) and lets them carry on to the cart or checkout.
 *   - chosen gift lines carry _gwp (gift variant) and _gwp_choice (machine product id), and are
 *     removed if the machine is removed or its quantity drops.
 *   - "No thanks" is stored in the hidden cart attribute _gwp_declined, and cleared again if that
 *     machine is removed, so adding it back starts the pop-up again.
 *   - several gift products in one cart each get their own gift (single gifts auto-add, choice
 *     products ask one at a time, starting with the product just added).
 *
 * Gift lines carry hidden properties, so a product bought separately at full price is never touched.
 * Cart + metafield data comes from sections/gwp-cart-data.liquid via the Section Rendering API.
 * Price is NOT set here: the GWP discount makes the gift $0 at checkout.
 * Debug: add ?gwp_debug=1 to any URL and watch the browser console.
 */
(function () {
  'use strict';
  if (window.__gwpAutoAdd) return;
  window.__gwpAutoAdd = true;

  var ROOT = (window.theme && window.theme.routes && window.theme.routes.root) ||
             (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  if (ROOT.charAt(ROOT.length - 1) !== '/') ROOT += '/';

  var SECTION = 'gwp-cart-data';
  var GWP_PROP = '_gwp';
  var CHOICE_PROP = '_gwp_choice';
  var DECLINED_ATTR = '_gwp_declined';
  var LABEL_PROP = 'Offer';
  var LABEL_VALUE = 'Free gift with purchase';
  var CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var DEBUG = /[?&]gwp_debug=1/.test(location.search);

  // The fetch in place before we wrap it (includes the theme's cart-count wrapper,
  // so the header count still updates when we add or remove gifts).
  var baseFetch = window.fetch.bind(window);
  var me = document.currentScript;
  var startCount = me ? parseInt(me.getAttribute('data-cart-count') || '1', 10) : 1;
  var timer = null;
  var pending = [];        // machines still waiting for a gift choice
  var lastDeclined = '';
  var modalOpen = false;
  var resumeCheckout = null;

  function log() { if (DEBUG) console.log.apply(console, ['[GWP]'].concat([].slice.call(arguments))); }

  function readCart() {
    return baseFetch(ROOT + '?sections=' + SECTION, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[SECTION];
        if (!html) throw new Error('section missing');
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('[data-gwp-cart]');
        return JSON.parse(el.textContent);
      });
  }

  function post(path, body) {
    return baseFetch(ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (d) {
        if (!res.ok) throw new Error((d && (d.description || d.message)) || res.status);
        return d;
      });
    });
  }

  // Final-chance reminders closed with the X: those machines aren't asked about again this visit
  // (kept per browser tab, and forgotten if the machine is removed from the cart)
  var DISMISS_KEY = 'gwp_dismissed';
  function getDismissed() {
    try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]') || []; } catch (e) { return []; }
  }
  function setDismissed(list) {
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function declinedList(str) {
    return String(str || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  // Bring gift lines in line with the cart. Resolves true if the cart was changed.
  async function doSync() {
    var cart = await readCart();
    var allowance = {};   // single gift variant id -> qty earned by qualifying lines
    var giftLines = {};   // single gift variant id -> [lines carrying _gwp, no _gwp_choice]
    var choiceWant = {};  // machine product id -> machine qty (choice offers)
    var choiceInfo = {};  // machine product id -> { title, options }
    var choiceLines = {}; // machine product id -> [chosen gift lines]
    var updates = {};
    var adds = [];
    var declined = declinedList(cart.declined);
    lastDeclined = cart.declined || '';

    cart.items.forEach(function (it) {
      if (it.gwpChoice) {
        (choiceLines[it.gwpChoice] = choiceLines[it.gwpChoice] || []).push(it);
      } else if (it.gwp) {
        (giftLines[it.variant_id] = giftLines[it.variant_id] || []).push(it);
      } else if (it.choice) {
        var pid = String(it.product_id);
        choiceWant[pid] = (choiceWant[pid] || 0) + it.quantity;
        choiceInfo[pid] = { title: it.title, options: it.options };
      } else if (it.gift && it.giftLive) {
        allowance[it.gift] = (allowance[it.gift] || 0) + it.quantity;
      }
    });

    // Single gifts: add or trim to match
    var ids = Object.keys(allowance).concat(Object.keys(giftLines))
      .filter(function (v, i, a) { return a.indexOf(v) === i; });
    ids.forEach(function (id) {
      var want = allowance[id] || 0;
      var lines = giftLines[id] || [];
      var have = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
      log('gift', id, { want: want, have: have });
      if (want > have) {
        var props = {};
        props[GWP_PROP] = String(id);
        props[LABEL_PROP] = LABEL_VALUE;
        adds.push({ id: Number(id), quantity: want - have, properties: props });
      } else if (want < have) {
        var left = want;
        lines.forEach(function (l) {
          var q = Math.min(l.quantity, left);
          left -= q;
          if (q !== l.quantity) updates[l.key] = q;
        });
      }
    });

    // "No thanks" only lasts while that machine is in the cart: once it's removed, forget the
    // answer so the pop-up starts again if they add it back
    var keepDeclined = declined.filter(function (pid) { return (choiceWant[pid] || 0) > 0; });
    if (keepDeclined.length !== declined.length) {
      var dAttrs = {};
      dAttrs[DECLINED_ATTR] = keepDeclined.join(',');
      try { await post('cart/update.js', { attributes: dAttrs }); } catch (e) { /* try again next sync */ }
      declined = keepDeclined;
      lastDeclined = keepDeclined.join(',');
      log('declined cleared for removed machines, now:', lastDeclined);
    }

    var dismissed = getDismissed().filter(function (pid) { return (choiceWant[pid] || 0) > 0; });
    setDismissed(dismissed);

    // Chosen gifts: trim if the machine was removed or reduced; work out who still needs to choose
    var nextPending = [];
    Object.keys(choiceWant).concat(Object.keys(choiceLines))
      .filter(function (v, i, a) { return a.indexOf(v) === i; })
      .forEach(function (pid) {
        var want = choiceWant[pid] || 0;
        var lines = choiceLines[pid] || [];
        var have = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
        log('choice', pid, { want: want, have: have });
        if (have > want) {
          var left = want;
          lines.forEach(function (l) {
            var q = Math.min(l.quantity, left);
            left -= q;
            if (q !== l.quantity) updates[l.key] = q;
          });
        } else if (want > have && declined.indexOf(pid) === -1 && dismissed.indexOf(pid) === -1 && choiceInfo[pid]) {
          nextPending.push({ pid: pid, title: choiceInfo[pid].title, options: choiceInfo[pid].options });
        }
      });
    pending = nextPending;

    var changed = false;
    if (Object.keys(updates).length) {
      await post('cart/update.js', { updates: updates });
      changed = true;
    }
    if (adds.length) {
      try {
        await post('cart/add.js', { items: adds });
        changed = true;
      } catch (err) {
        console.warn('[GWP] Could not add gift:', err.message);
      }
    }
    log('sync done, changed:', changed, 'pending choices:', pending.length);
    return changed;
  }

  // One sync at a time; callers queue behind the one in progress
  var chain = Promise.resolve();
  function sync() {
    var run = chain.then(doSync).catch(function (err) {
      console.warn('[GWP] sync failed:', err);
      return false;
    });
    chain = run;
    return run;
  }

  // Background sync (page load, tab return, XHR): tell the theme to redraw if needed
  function schedule(delay, after) {
    clearTimeout(timer);
    timer = setTimeout(function () {
      sync().then(function (changed) {
        if (changed) refreshCartUI();
        if (after) after();
      });
    }, typeof delay === 'number' ? delay : 120);
  }

  // reopenDrawer: after a gift is claimed from the pop-up. Palo Alto doesn't redraw its cart drawer
  // for changes it didn't make itself, so reload the page and open the drawer again once it loads.
  function refreshCartUI(reopenDrawer) {
    ['theme:cart:refresh', 'theme:cart:reload', 'cart:refresh'].forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    });
    // Full cart page: reload so totals and lines are exact
    if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
    if (reopenDrawer) {
      try { sessionStorage.setItem('gwp_open_cart', '1'); } catch (e) {}
      location.reload();
    }
  }

  function openCartDrawer() {
    var list = document.querySelectorAll('[data-cart-drawer-toggle], a[href="#cart-drawer"]');
    for (var i = 0; i < list.length; i++) {
      if (!list[i].closest('#cart-drawer')) { list[i].click(); return; }
    }
  }

  /* ---------- Swipe rows (product page gift box and pop-up) ---------- */

  // Shows arrows/dots only when the row actually scrolls, and keeps the active dot in step
  function swipeUpdate(list) {
    var wrap = list.closest('[data-gwp-swipe-wrap]');
    if (!wrap) return;
    var max = list.scrollWidth - list.clientWidth;
    var scrollable = max > 4;
    wrap.classList.toggle('is-swipe', scrollable);
    var prev = wrap.querySelector('[data-gwp-prev]');
    var next = wrap.querySelector('[data-gwp-next]');
    if (prev) prev.hidden = !scrollable || list.scrollLeft <= 4;
    if (next) next.hidden = !scrollable || list.scrollLeft >= max - 4;
    var dots = wrap.querySelectorAll('[data-gwp-dots] span');
    var items = list.children;
    if (!dots.length || !items.length) return;
    var step = items.length > 1 ? (items[1].offsetLeft - items[0].offsetLeft) : list.clientWidth;
    var idx = list.scrollLeft >= max - 4 ? items.length - 1 : Math.round(list.scrollLeft / (step || 1));
    for (var i = 0; i < dots.length; i++) dots[i].classList.toggle('is-active', i === idx);
  }

  function swipeAll() { document.querySelectorAll('[data-gwp-swipe]').forEach(swipeUpdate); }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-gwp-prev], [data-gwp-next]');
    if (!btn) return;
    var wrap = btn.closest('[data-gwp-swipe-wrap]');
    var list = wrap && wrap.querySelector('[data-gwp-swipe]');
    if (!list) return;
    e.preventDefault();
    var items = list.children;
    var step = items.length > 1 ? (items[1].offsetLeft - items[0].offsetLeft) : list.clientWidth;
    list.scrollBy({ left: btn.hasAttribute('data-gwp-next') ? step : -step, behavior: 'smooth' });
  });
  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-gwp-swipe]')) swipeUpdate(e.target);
  }, true);
  window.addEventListener('resize', swipeAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', swipeAll); else swipeAll();
  window.addEventListener('load', swipeAll);

  /* ---------- Gift picker pop-up ---------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var GIFT_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';

  function closeModal(el) {
    if (el && el.parentNode) el.parentNode.removeChild(el);
    document.documentElement.classList.remove('gwp-pick-lock');
    modalOpen = false;
  }

  // reminder: true when shown from the cart / checkout rather than straight after adding
  function openPicker(reminder) {
    if (modalOpen || !pending.length) return false;
    var p = pending[0];
    if (!p.options || p.options.length < 2) return false;
    modalOpen = true;

    var heading = reminder ? 'Final chance to claim your free gift' : 'Choose your free gift';
    var intro = 'Your ' + esc(p.title) + ' comes with a free gift. Choose 1 of these ' + p.options.length + ' gifts and we\'ll add it to your cart at no cost.';
    var cards = p.options.map(function (o) {
      return '<button type="button" class="gwp-pick__opt" data-gwp-opt="' + o.id + '" aria-pressed="false">' +
        '<span class="gwp-pick__img">' + (o.image ? '<img src="' + esc(o.image) + '" alt="" loading="lazy">' : '') + '</span>' +
        '<span class="gwp-pick__name">' + esc(o.name) + '</span>' +
        (o.value ? '<span class="gwp-pick__value">Valued at ' + esc(o.value) + '</span>' : '') +
        '<span class="gwp-pick__tick" aria-hidden="true">&#10003;</span>' +
        '</button>';
    }).join('');

    var el = document.createElement('div');
    el.className = 'gwp-pick';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', 'gwp-pick-title');
    el.innerHTML =
      '<div class="gwp-pick__panel">' +
        '<button type="button" class="gwp-pick__close" aria-label="Close" data-gwp-close>&times;</button>' +
        '<div class="gwp-pick__head">' +
          '<span class="gwp-pick__icon">' + GIFT_ICON + '</span>' +
          '<div><p class="gwp-pick__title" id="gwp-pick-title">' + heading + '</p>' +
          '<p class="gwp-pick__intro">' + intro + '</p></div>' +
        '</div>' +
        '<div class="gwp-pick__swipe gwp-swipe-wrap" data-gwp-swipe-wrap>' +
          '<div class="gwp-pick__grid gwp-pick__grid--' + p.options.length + '" data-gwp-swipe>' + cards + '</div>' +
          '<button type="button" class="gwp-choices__arrow gwp-choices__arrow--prev" aria-label="Previous gift" data-gwp-prev hidden>&#8249;</button>' +
          '<button type="button" class="gwp-choices__arrow gwp-choices__arrow--next" aria-label="Next gift" data-gwp-next hidden>&#8250;</button>' +
          '<div class="gwp-choices__dots" aria-hidden="true" data-gwp-dots>' +
            p.options.map(function (o, i) { return '<span' + (i === 0 ? ' class="is-active"' : '') + '></span>'; }).join('') +
          '</div>' +
        '</div>' +
        '<p class="gwp-pick__note">Free gifts available whilst stocks last.</p>' +
        '<p class="gwp-pick__error" data-gwp-error hidden></p>' +
        '<div class="gwp-pick__actions">' +
          '<button type="button" class="gwp-pick__claim" data-gwp-claim disabled>Claim my gift</button>' +
          '<button type="button" class="gwp-pick__no" data-gwp-no>No thanks</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(el);
    document.documentElement.classList.add('gwp-pick-lock');
    el.querySelectorAll('[data-gwp-swipe]').forEach(swipeUpdate);

    var chosen = null;
    var claimBtn = el.querySelector('[data-gwp-claim]');
    var noBtn = el.querySelector('[data-gwp-no]');
    var errEl = el.querySelector('[data-gwp-error]');

    // X / Escape / tapping outside. After adding: just close (the cart and checkout will remind them).
    // On the final-chance reminder: stop asking about this machine and let them carry on.
    function closeIt() {
      closeModal(el);
      if (!reminder) { resumeCheckout = null; return; }
      var list = getDismissed();
      if (list.indexOf(p.pid) === -1) list.push(p.pid);
      setDismissed(list);
      pending = pending.filter(function (x) { return x.pid !== p.pid; });
      if (pending.length) { openPicker(true); return; }
      if (resumeCheckout) { var go = resumeCheckout; resumeCheckout = null; go(); }
    }

    function done(changedCart) {
      closeModal(el);
      sync().then(function (changed) {
        // Checkout was paused and nothing else is waiting: carry on to checkout
        if (resumeCheckout && !pending.length) { var go = resumeCheckout; resumeCheckout = null; go(); return; }
        // Gift added: reload and reopen the cart so it shows (any other machine still waiting is
        // asked about again when the cart opens)
        if (changed || changedCart) { refreshCartUI(true); return; }
        if (pending.length) openPicker(reminder);
      });
    }

    el.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-gwp-opt]');
      if (opt) {
        el.querySelectorAll('[data-gwp-opt]').forEach(function (b) {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        opt.classList.add('is-selected');
        opt.setAttribute('aria-pressed', 'true');
        chosen = opt.getAttribute('data-gwp-opt');
        claimBtn.disabled = false;
        errEl.hidden = true;
        return;
      }
      if (e.target.closest('[data-gwp-close]') || e.target === el) {
        closeIt();
        return;
      }
      if (e.target.closest('[data-gwp-claim]')) {
        if (!chosen) return;
        claimBtn.disabled = true;
        noBtn.disabled = true;
        claimBtn.textContent = 'Adding your gift…';
        var props = {};
        props[GWP_PROP] = String(chosen);
        props[CHOICE_PROP] = String(p.pid);
        props[LABEL_PROP] = LABEL_VALUE;
        post('cart/add.js', { items: [{ id: Number(chosen), quantity: 1, properties: props }] })
          .then(function () { done(true); })
          .catch(function (err) {
            console.warn('[GWP] Could not add chosen gift:', err.message);
            errEl.textContent = 'Sorry, that gift couldn\'t be added. Please choose another or try again.';
            errEl.hidden = false;
            claimBtn.textContent = 'Claim my gift';
            claimBtn.disabled = false;
            noBtn.disabled = false;
          });
        return;
      }
      if (e.target.closest('[data-gwp-no]')) {
        claimBtn.disabled = true;
        noBtn.disabled = true;
        var list = declinedList(lastDeclined);
        if (list.indexOf(p.pid) === -1) list.push(p.pid);
        var attrs = {};
        attrs[DECLINED_ATTR] = list.join(',');
        post('cart/update.js', { attributes: attrs })
          .then(function () { done(false); })
          .catch(function () { done(false); });
      }
    });

    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeIt();
    });
    setTimeout(function () { var f = el.querySelector('[data-gwp-opt]'); if (f) f.focus(); }, 50);
    return true;
  }

  /* ---------- Cart writes ---------- */

  // After we changed gifts, hand the theme a response that already includes them
  function requestedSections(body) {
    var out = {};
    try {
      if (!body) return out;
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (body.get('sections')) out.sections = body.get('sections');
        if (body.get('sections_url')) out.sections_url = body.get('sections_url');
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        if (body.get('sections')) out.sections = body.get('sections');
        if (body.get('sections_url')) out.sections_url = body.get('sections_url');
      } else if (typeof body === 'string') {
        if (body.charAt(0) === '{') {
          var j = JSON.parse(body);
          if (j.sections) out.sections = j.sections;
          if (j.sections_url) out.sections_url = j.sections_url;
        } else {
          var u = new URLSearchParams(body);
          if (u.get('sections')) out.sections = u.get('sections');
          if (u.get('sections_url')) out.sections_url = u.get('sections_url');
        }
      }
    } catch (e) { /* ignore */ }
    return out;
  }

  function freshResponse(res, init) {
    return res.clone().json().then(function (data) {
      var payload = { updates: {} };
      var secs = requestedSections(init && init.body);
      if (secs.sections) payload.sections = secs.sections;
      if (secs.sections_url) payload.sections_url = secs.sections_url;
      return post('cart/update.js', payload).then(function (fresh) {
        var out;
        if (data && Array.isArray(data.items) && data.item_count !== undefined) {
          out = fresh;                       // change/update responses are a cart
        } else {
          out = data;                        // add responses are the added item(s)
          if (fresh.sections) out.sections = fresh.sections;
        }
        var headers = new Headers(res.headers);
        headers.set('Content-Type', 'application/json');
        return new Response(JSON.stringify(out), { status: res.status, statusText: res.statusText, headers: headers });
      });
    }).catch(function () { return res; });
  }

  // Product ids in a /cart/add response (one item, or { items: [...] })
  function addedProductIds(res) {
    return res.clone().json().then(function (d) {
      var list = d && Array.isArray(d.items) ? d.items : [d];
      return list.map(function (i) { return i && i.product_id ? String(i.product_id) : ''; }).filter(Boolean);
    }).catch(function () { return []; });
  }

  // Ask about the product just added first, then any others still waiting
  function putFirst(ids) {
    if (!ids || !ids.length) return;
    pending.sort(function (a, b) {
      return (ids.indexOf(b.pid) > -1 ? 1 : 0) - (ids.indexOf(a.pid) > -1 ? 1 : 0);
    });
  }

  // Theme cart changes: finish the gift update BEFORE the theme draws the cart drawer.
  // After an add, open the gift picker if the new machine offers a choice.
  window.fetch = function (input, init) {
    var url = '';
    try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
    if (!CART_WRITE_RE.test(url)) return baseFetch.apply(window, arguments);
    var isAdd = CART_ADD_RE.test(url);

    return baseFetch.apply(window, arguments).then(function (res) {
      if (!res.ok) { schedule(); return res; }
      var added = isAdd ? addedProductIds(res) : Promise.resolve([]);
      return added.then(function (ids) {
        return sync().then(function (changed) {
          if (isAdd && pending.length) {
            putFirst(ids);
            setTimeout(function () { openPicker(false); }, 350);
          }
          return changed ? freshResponse(res, init) : res;
        });
      });
    });
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      if (CART_WRITE_RE.test(String(url))) {
        var isAdd = CART_ADD_RE.test(String(url));
        this.addEventListener('loadend', function () {
          schedule(120, function () { if (isAdd && pending.length) openPicker(false); });
        });
      }
    } catch (e) {}
    return xhrOpen.apply(this, arguments);
  };

  /* ---------- Reminders: opening the cart and pressing checkout ---------- */

  // Palo Alto opens the cart drawer from links with data-cart-drawer-toggle (href="#cart-drawer");
  // the same attribute is on the drawer's close button, so clicks inside the drawer are ignored.
  var CART_OPEN_SEL = '[data-cart-drawer-toggle], a[href="#cart-drawer"], .cart__toggle, [data-cart-toggle], [data-drawer-toggle="cart"], a[href="/cart"], a[href$="/cart"]';

  // Reminder after the cart opens. Uses its own timer so a background sync can't cancel it.
  function remindSoon() {
    setTimeout(function () {
      sync().then(function (changed) {
        if (changed) refreshCartUI();
        if (pending.length) openPicker(true);
      });
    }, 300);
  }
  var CHECKOUT_SEL = '[name="checkout"], button[name="checkout"], a[href*="/checkout"], [data-checkout-button], .cart__checkout';

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    // Checkout: pause it while someone still has a gift to choose
    var co = e.target.closest(CHECKOUT_SEL);
    if (co && !co.hasAttribute('data-gwp-pass')) {
      if (pending.length && !modalOpen) {
        e.preventDefault();
        e.stopPropagation();
        resumeCheckout = function () {
          co.setAttribute('data-gwp-pass', '');
          co.click();
          setTimeout(function () { co.removeAttribute('data-gwp-pass'); }, 1000);
        };
        openPicker(true);
      }
      return;
    }

    // Cart icon / cart link: remind them once the drawer is open (not the drawer's own close button)
    var opener = e.target.closest(CART_OPEN_SEL);
    if (opener && !opener.closest('#cart-drawer, .gwp-pick')) remindSoon();
  }, true);

  // Cached pages: hide any badge or notice whose end date has passed (Adelaide time)
  try {
    var today = parseInt(new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Adelaide' })
      .format(new Date()).replace(/-/g, ''), 10);
    document.querySelectorAll('[data-gwp-end]').forEach(function (el) {
      if (today > parseInt(el.getAttribute('data-gwp-end'), 10)) el.style.display = 'none';
    });
  } catch (e) { /* ignore */ }

  // Reopen the cart drawer after the reload that follows claiming a gift
  try {
    if (sessionStorage.getItem('gwp_open_cart')) {
      sessionStorage.removeItem('gwp_open_cart');
      var reopen = function () { setTimeout(openCartDrawer, 300); };
      if (document.readyState === 'complete') reopen(); else window.addEventListener('load', reopen);
    }
  } catch (e) { /* ignore */ }

  // Page load (covers non-AJAX form posts), back/forward cache and returning to the tab.
  // On the cart page, remind straight away.
  var onCartPage = /\/cart\/?$/.test(location.pathname);
  if (startCount > 0) schedule(0, function () { if (onCartPage && pending.length) openPicker(true); });
  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') schedule(); });
})();
